import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, importProvidersFrom } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, MatNativeDateModule } from '@angular/material/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, Router, RouteReuseStrategy, withRouterConfig } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';

import {
  CustomRouteReuseStrategy,
  matGlobalRippleConfig,
  PU_CONFIG,
  PU_DIALOG_OK_CONFIG,
  PU_TOAST_CONFIG,
  PU_TOAST_DEFAULT_CONFIG,
  SPINNER_COMPONENT,
  TranslateLoaderService,
} from '@hglobal/common';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { provideNgxMask } from 'ngx-mask';
import { NgxPermissionsModule } from 'ngx-permissions';

import { SpinnerComponent } from '@pp/common';
import { AUTH_API, AuthApiService } from '@pp/core/auth-store';
import { AppErrorsInterceptor, AuthKeyInterceptor, OrgIdInterceptor } from '@pp/core/interceptors';
import { ConfigService, DateFilterService, GlobalToastService, PpDialogService, RecaptchaService } from '@pp/core/services';
import { DICTIONARIES_API, DictionariesApiService, DictionariesMockApi, DictionariesService, DictionariesStore } from '@pp/dictionaries';
import { environment } from '@pp/environment';
import { SHELL_API, ShellApi, ShellMockApi, ShellStore } from '@pp/shell';

import { appRoutes } from './app.routes';

const isLocalMode = location.hostname === 'localhost';

const APP_HTTP_INTERCEPTORS = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthKeyInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AppErrorsInterceptor,
    multi: true,
  },
  provideHttpClient(withInterceptors([OrgIdInterceptor])),
];

const SENTRY = [
  {
    provide: ErrorHandler,
    useValue: Sentry.createErrorHandler({
      showDialog: true,
    }),
  },
  {
    provide: Sentry.TraceService,
    deps: [Router],
  },
  {
    provide: APP_INITIALIZER,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    useFactory: () => () => {},
    deps: [Sentry.TraceService],
    multi: true,
  },
];

const PU = [
  {
    provide: PU_CONFIG,
    useValue: {
      apiHost: environment.apiHost,
      useMocks: environment.useMocks,
      production: environment.production,
      assetsHost: environment.assetsHost,
    },
  },
  {
    provide: PU_TOAST_CONFIG,
    useValue: {
      ...PU_TOAST_DEFAULT_CONFIG,
      closeIconName: 'close',
      position: {
        top: -8,
        right: 8,
      },
    },
  },
  {
    provide: PU_DIALOG_OK_CONFIG,
    useValue: {
      iconSize: '32',
      iconCloseName: 'close',
      iconCloseSize: '24',
    },
  },
  {
    provide: SPINNER_COMPONENT,
    useValue: SpinnerComponent,
  },
];

const STORE = [DictionariesStore, ShellStore];

const NGX = [
  provideNgxMask(),
  importProvidersFrom(
    BrowserAnimationsModule,
    MatNativeDateModule,
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useClass: TranslateLoaderService,
        deps: [HttpClient],
      },
    }),
    NgxPermissionsModule.forRoot(),
  ),
];

const API = [
  {
    provide: DICTIONARIES_API,
    useClass: environment.useMocks ? DictionariesMockApi : DictionariesApiService,
  },
  {
    provide: SHELL_API,
    useClass: environment.useMocks ? ShellMockApi : ShellApi,
  },
  {
    provide: AUTH_API,
    useClass: AuthApiService,
  },
];

export const ROUTING_CONFIG = [
  provideRouter(
    appRoutes,
    withRouterConfig({
      paramsInheritanceStrategy: 'always',
    }),
  ),
  {
    provide: RouteReuseStrategy,
    useClass: CustomRouteReuseStrategy,
  },
];

export const CUSTOM_PROVIDERS_CONFIG = [
  ...APP_HTTP_INTERCEPTORS,
  ...SENTRY,
  ...PU,
  ...STORE,
  ...NGX,
  ...API,
  {
    provide: MAT_RIPPLE_GLOBAL_OPTIONS,
    useValue: matGlobalRippleConfig,
  },
  provideHttpClient(withInterceptorsFromDi()),
  provideServiceWorker('ngsw-worker.js', {
    enabled: !isLocalMode,
    registrationStrategy: 'registerWhenStable:30000',
  }),
];

export const SERVICES_PROVIDES = [
  PpDialogService,
  GlobalToastService,
  DictionariesService,
  DateFilterService,
  RecaptchaService,
  ConfigService,
];
